import { subdivisionOptions } from '../lib/subdivision-options';
import { FRENCH_SUBDIVISIONS, subdivisionIssue, type GeographicSubdivision } from '@erp/shared';
import { useTranslation } from 'react-i18next';
import { CatalogueSelect } from '@/components/common/catalogue-select';
import { Label } from '@/components/ui/label';
import { useAddressResolution } from '../api/use-address-resolution';

export function SubdivisionSelect({
  id,
  value,
  city,
  postalCode,
  region,
  onChange,
}: {
  id: string;
  value: GeographicSubdivision;
  city?: string | null | undefined;
  postalCode?: string | null | undefined;
  region?: string | null | undefined;
  onChange: (value: Pick<GeographicSubdivision, 'subdivisionCode' | 'subdivisionName'>) => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const query = useAddressResolution(value.countryCode, postalCode);
  if (value.countryCode !== 'FR') return null;
  const selected = FRENCH_SUBDIVISIONS.find((entry) => entry.code === value.subdivisionCode);
  const relevant =
    query.data?.data.filter(
      (entry) => entry.countryCode === value.countryCode && entry.postalCode === postalCode?.trim(),
    ) ?? [];
  const cityMatches = relevant.filter(
    (entry) => entry.city.toLocaleLowerCase() === city?.trim().toLocaleLowerCase(),
  );
  const candidates = cityMatches.length ? cityMatches : relevant;
  const conflict = Boolean(
    selected &&
    ((region && region !== selected.region) ||
      (candidates.length && candidates.every((entry) => entry.districtCode !== selected.code))),
  );
  const issue = subdivisionIssue(value);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{t('subdivision.label')}</Label>
      <CatalogueSelect
        id={id}
        label={t('subdivision.label')}
        value={value.subdivisionCode ?? ''}
        options={subdivisionOptions}
        ariaInvalid={Boolean(issue)}
        ariaDescribedBy={`${id}-hint${issue ? ` ${id}-error` : ''}${conflict ? ` ${id}-conflict` : ''}`}
        onChange={([code]) => {
          const entry = FRENCH_SUBDIVISIONS.find((row) => row.code === code);
          onChange({ subdivisionCode: entry?.code ?? null, subdivisionName: entry?.name ?? null });
        }}
      />
      {issue ? (
        <p id={`${id}-error`} role="alert" className="text-sm text-destructive">
          {t(issue)}
        </p>
      ) : null}
      {conflict ? (
        <p id={`${id}-conflict`} role="status" className="text-sm text-muted-foreground">
          {t('subdivision.conflict')}
        </p>
      ) : null}
      <p id={`${id}-hint`} className="text-xs text-muted-foreground">
        {t('subdivision.hint')}
      </p>
    </div>
  );
}
